import React, { useState, useEffect } from "react";
import { useParams } from "react-router";

import PersonDetail from "./PersonDetail";
import { SonicWorkInputData } from "../../../mypage/user_settings/database_entries/SonicWorkInputData";
import { parseAndValidWorks } from "../../../mypage/user_settings/database_entries/parseAndValidWorks";

import { ActivityHistoryData } from "../../../../common/classes/ActivityHistoryData";
import { ImageSourceSetData } from "../../../../common/classes/ImageSourceSet";
import { SonicTermData } from "../../../../common/classes/SonicTerm";

import { useAppSelector, useAppDispatch } from "../../../../redux/hooks";
import {
  getPersonAdmin,
  getPersonData,
} from "../../../../redux/reducers/person";
import { getAuthToken } from "../../../../redux/reducers/auth";
import {
  updatePersonAdmin,
  getPersonOwners,
} from "../../../../redux/reducers/person";

import { FieldState } from "../../../../utils/ValidationFunctions";
import { getTermOptions } from "../../../../utils/GeneralUtils";
import { RequestState } from "../../../../utils/RequestState";
import { TermType, useGetTerm } from "../../../../utils/useTerm";
import {
  fieldStateToUpdateArgs,
  validFieldState,
} from "../../../mypage/user_settings/FieldStateFunctions";
import { useGetGameSelection } from "../../../../utils/useGameSelection";
import {
  SonicGameDetail,
  SonicGameDetailData,
} from "../../../../common/classes/SonicGameDetail";

const PersonDetailContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const person = useAppSelector(getPersonData)[0];
  const owner = useAppSelector(getPersonOwners)[0];
  const auth_token = useAppSelector(getAuthToken);
  const dispatch = useAppDispatch();

  const playerTypeData = useGetTerm(TermType.PlayerTypes, undefined, -1)
    .termData.entries;

  const [fieldState, setFieldState] = useState<FieldState>();
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectStatus, setSelectStatus] = useState<string>();
  const [showStatusMenu, setShowStatusMenu] = useState<boolean>(false);
  const [verified, setVerified] = useState<boolean>(false);
  const [image, setImage] = useState<File | ImageSourceSetData>();
  const [deleteImage, setDeleteImage] = useState<string>();
  const [historyItems, setHistoryItems] = useState<Array<ActivityHistoryData>>(
    []
  );
  const [requestState, setRequestState] = useState<RequestState>(
    RequestState.Waiting
  );

  // MyGame,Team
  const [games, setGames] = useState<Array<SonicTermData>>();
  const [team, setTeam] = useState<SonicTermData>();

  const handleGames = (value: Array<SonicTermData>) => {
    setGames([...value]);
  };

  const handleTeam = (value: SonicTermData) => {
    setTeam(value);
  };

  const [isEditWork, setIsEditWork] = useState<boolean>(false);
  const [editWorkNumber, setEditWorkNumber] = useState<number>(0);
  const [workItems, setWorkItems] = useState<Array<SonicWorkInputData>>([]);

  const addWorkInput = () => {
    if (workItems.length > 0) {
      setEditWorkNumber(workItems.length);
    } else {
      setEditWorkNumber(0);
    }

    setIsEditWork(true);
    setRequestState(RequestState.Pending);
  };

  const deleteWork = (i: number) => {
    const newList = workItems.filter((v, index) => index !== i);
    setWorkItems(newList);
    setRequestState(RequestState.Pending);
  };

  const editWork = (i: number, work: SonicWorkInputData) => {
    const newList = workItems;
    newList[i] = work;
    setWorkItems([...newList]);
    setRequestState(RequestState.Pending);
  };

  useEffect(() => {
    if (auth_token) {
      setIsLoading(true);

      const personPromise = dispatch(
        getPersonAdmin({
          auth_token: auth_token,
          id: id,
          is_jikei_request: false,
        })
      );

      personPromise.finally(() => setIsLoading(false));

      return () => {
        personPromise.abort();
      };
    }
  }, []);

  useEffect(() => {
    if (!person || person.id !== id) return;

    if (person.verified === null || person.verified === undefined) {
      setVerified(false);
    } else {
      setVerified(person.verified);
    }

    if (person.imgs?.[0]) {
      setImage(person.imgs[0]);
    }

    if (person.history) {
      const newHistory: Array<ActivityHistoryData> = [];
      person.history.forEach((el, i) => {
        newHistory[i] = { year: el.year, title: el.title, error: false };
      });
      setHistoryItems(newHistory);
    }

    if (person.works) {
      const newWork: Array<SonicWorkInputData> = [];
      person.works.forEach((el, i) => {
        newWork[i] = {
          title: el.title,
          description: el.description,
          link: el.link,
          image: el.image || undefined,
          error: false,
        };
      });
      setWorkItems(newWork);
    }

    if (person.game) {
      setGames(person.game);
    }

    if (person.team) {
      setTeam(person.team[0]);
    }
  }, [person]);

  const handleStatusMenu = () => {
    setShowStatusMenu(!showStatusMenu);
  };

  const handleHistoryInputChange = (
    index: number,
    prop: string,
    value?: string
  ) => {
    const history = historyItems;

    switch (prop) {
      case "year":
        history[index].year = value;
        break;
      case "title":
        history[index].title = value;
        break;
      default:
        break;
    }

    if (!history[index].title && history[index].year) {
      history[index].error = true;
    } else {
      history[index].error = false;
      setRequestState(RequestState.Pending);
    }

    setHistoryItems([...history]);
  };

  const parseAndValidPlayerHistory = (update_args: {
    [key: string]: string | File;
  }) => {
    const update_obj: Array<{ year?: string; title: string }> = [];
    let isError = false;
    if (historyItems.length > 0) {
      for (const el of historyItems) {
        if (el.error == true) {
          isError = true;
          break;
        }

        if (el.title) {
          update_obj.push({ year: el.year, title: el.title });
        }
      }

      if (isError === true) {
        return false;
      }

      update_args["sonic_history"] = JSON.stringify(update_obj);
      return true;
    } else {
      update_args["sonic_history"] = JSON.stringify([]);
      return true;
    }
  };

  const handleUpdate = () => {
    if (
      auth_token &&
      id &&
      (fieldState !== undefined ||
        selectStatus !== undefined ||
        historyItems !== undefined)
    ) {
      const update_args: { [key: string]: string | File } = {};

      if (!validFieldState("edit", ["sonic_name"], fieldState)) {
        setError(true);
        return;
      }

      if (!parseAndValidPlayerHistory(update_args)) {
        setError(true);
        return;
      }

      if (!parseAndValidWorks(update_args, workItems)) {
        setError(true);
        return;
      }

      if (games && games.length > 0) {
        const gameIds: Array<string> = [];
        games.forEach((game) => gameIds.push(game.id));
        update_args["sonic_game"] = JSON.stringify(gameIds);
      } else {
        update_args["sonic_game"] = JSON.stringify([]);
      }

      update_args["sonic_team"] = team ? team.id : "";

      if (gameDetails) {
        update_args["sonic_game_details"] = JSON.stringify(
          SonicGameDetail.toRequestArgs(gameDetails, gameSelections)
        );
      }

      setError(false);
      setRequestState(RequestState.Doing);

      if (image instanceof File) {
        update_args["sonic_imgs"] = image;
      }

      if (deleteImage) {
        update_args["sonic_delete_imgs"] = deleteImage;
      }

      if (selectStatus) {
        update_args["sonic_status"] = selectStatus;
      }

      update_args["sonic_verified"] = verified.toString();

      fieldStateToUpdateArgs(update_args, fieldState);

      const promise = dispatch(
        updatePersonAdmin({
          auth_token: auth_token,
          id: id,
          update: update_args,
          is_jikei_request: false,
        })
      );

      promise.finally(() => setRequestState(RequestState.Success));

      setShowStatusMenu(false);
      setSelectStatus("");
      setDeleteImage(undefined);
    }
  };

  const handleInputChange = (
    value: File | string | undefined,
    name: string
  ) => {
    let f: FieldState = {};
    if (fieldState !== undefined) {
      f = fieldState;
    }

    if (value === undefined) {
      if (name !== "sonic_imgs") f[name] = ["", false];
    } else {
      if (value instanceof File) {
        setImage(value);
      } else {
        f[name] = [value, true];
      }
      setRequestState(RequestState.Pending);
    }
    setFieldState({ ...f });
  };

  const handleDeleteImage = () => {
    if (image && !(image instanceof File)) {
      setDeleteImage(image.id.toString());
    }

    setImage(undefined);
  };

  // Game Details
  const { gameSelections, isLoading: isGameDetailLoading } =
    useGetGameSelection();
  const [gameDetails, setGameDetails] = useState<Array<SonicGameDetailData>>();

  useEffect(() => {
    if (error) {
      setError(false);
    }
  }, [fieldState, historyItems, workItems]);

  return (
    <PersonDetail
      error={error}
      person={person}
      owner={owner}
      image={image}
      deleteImage={handleDeleteImage}
      isLoading={isLoading}
      selectStatus={
        typeof fieldState?.["sonic_status"]?.[0] === "string"
          ? fieldState["sonic_status"][0]
          : ""
      }
      setSelectStatus={handleInputChange}
      handleUpdate={handleUpdate}
      typeOptions={getTermOptions(playerTypeData)}
      games={games}
      handleGames={handleGames}
      team={team}
      handleTeam={handleTeam}
      showStatusMenu={showStatusMenu}
      handleStatusMenu={handleStatusMenu}
      handleInputChange={handleInputChange}
      historyItems={historyItems}
      handleHistoryInputChange={handleHistoryInputChange}
      verified={verified}
      setVerified={setVerified}
      requestState={requestState}
      setRequestState={setRequestState}
      workItems={workItems}
      isEditWork={isEditWork}
      editWorkNumber={editWorkNumber}
      addWork={addWorkInput}
      deleteWork={deleteWork}
      editWork={editWork}
      setIsEditWork={setIsEditWork}
      setEditWorkNumber={setEditWorkNumber}
      gameDetailLoading={isGameDetailLoading}
      gameDetailOptions={gameSelections}
      handleGameDetails={(details: Array<SonicGameDetailData>) => {
        setRequestState(RequestState.Pending);
        setGameDetails([...details]);
      }}
    />
  );
};

export default PersonDetailContainer;
